<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { maskCurrency, parseCurrency } from "@/utils/masks";
import AdesaoGateModal from "./components/AdesaoGateModal.vue";
import FileViewerModal from "./components/FileViewerModal.vue";
import EmptyState from "@/components/widgets/empty-state";

export default {
  components: { Layout, AdesaoGateModal, FileViewerModal, EmptyState },
  data() {
    return {
      loading:      false,
      loadError:    false,
      leads:        [],

      viewerVisible: false,
      viewerLead:    null,

      paymentModal:       false,
      paymentLead:        null,
      paymentFile:        null,
      paymentPreview:     "",
      paymentPreviewIsPdf: false,
      paymentCommission:  "",
      systemCommission:   0,
      paymentError:       "",
      confirming:         false,
      fields: [
        { key: "afiliado_name",          label: "Afiliado" },
        { key: "name",                   label: "Lead" },
        { key: "contact",                label: "Contato" },
        { key: "vehicle",                label: "Veículo" },
        { key: "commission_value",       label: "Comissão" },
        { key: "date_converted",         label: "Convertido em" },
        { key: "consultant_payment_date",label: "Pagamento" },
        { key: "actions",                label: "" },
      ],
    };
  },
  computed: {
    commissionValue() {
      return parseCurrency(this.paymentCommission);
    },
    commissionState() {
      if (!this.paymentCommission) return null;
      return this.commissionValue >= this.systemCommission ? null : false;
    },
  },
  created() {
    this.load();
  },
  methods: {
    maskCurrency,

    load() {
      this.loading   = true;
      this.loadError = false;
      api.get("afiliados/user-leads?status=converted")
        .then((r) => {
          if (r.data.status === "success") {
            this.leads = r.data.list;
          } else {
            this.loadError = true;
          }
        })
        .catch(() => { this.loadError = true; })
        .finally(() => { this.loading = false; });
    },

    openProofViewer(lead) {
      this.viewerLead    = lead;
      this.viewerVisible = true;
    },

    openPaymentModal(lead) {
      this.paymentLead      = lead;
      this.paymentFile      = null;
      this.paymentPreview   = "";
      this.paymentError     = "";
      this.systemCommission = parseFloat(lead.commission_value) || 0;
      this.paymentCommission = maskCurrency(String(Math.round(this.systemCommission * 100)));
      this.paymentModal     = true;
    },

    onPaymentFileChange(e) {
      const f = e.target.files && e.target.files[0];
      this.paymentFile = f || null;
      if (this.paymentFile && (this.paymentFile.type.startsWith("image/") || this.paymentFile.type === "application/pdf")) {
        this.paymentPreview      = URL.createObjectURL(this.paymentFile);
        this.paymentPreviewIsPdf = this.paymentFile.type === "application/pdf";
      } else {
        this.paymentPreview      = "";
        this.paymentPreviewIsPdf = false;
      }
    },

    confirmPayment() {
      if (!this.paymentFile || !this.paymentLead) {
        this.paymentError = "Selecione o comprovante de pagamento (PDF ou imagem).";
        return;
      }
      if (this.commissionState === false) {
        this.paymentError = "A comissão não pode ser menor do que o valor apurado pelo sistema.";
        return;
      }
      this.paymentError = "";
      this.confirming    = true;

      const formData = new FormData();
      formData.append("file", this.paymentFile);
      formData.append("commission_value", this.commissionValue);

      api.post(`afiliados/leads/${this.paymentLead.id}/payment/confirm`, formData)
        .then((r) => {
          if (r.data.status === "success") {
            const lead = this.paymentLead;
            lead.consultant_payment_date = new Date().toISOString();
            lead.payment_proof_url       = r.data.proof_url;
            lead.payment_proof_name      = r.data.proof_name;
            lead.commission_value        = r.data.commission_value;
            this.paymentModal = false;
          } else {
            this.paymentError = r.data.message || "Erro ao confirmar pagamento.";
          }
        })
        .catch((e) => {
          this.paymentError = (e.response && e.response.data && e.response.data.message) || "Erro ao confirmar pagamento. Tente novamente.";
        })
        .finally(() => { this.confirming = false; });
    },

    vehicleLabel(lead) {
      return [lead.vehicle_type, lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year]
        .filter(Boolean).join(" ") || "—";
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("pt-BR") : "—";
    },
    formatMoney(v) {
      return "R$ " + parseFloat(v || 0).toFixed(2).replace(".", ",");
    },
  },
};
</script>

<template>
  <Layout>
    <AdesaoGateModal />
    <div class="page-title-box d-flex align-items-center justify-content-between mb-4">
      <h3 class="mb-0">Leads Convertidos</h3>
      <span class="badge badge-soft-success font-size-12">{{ leads.length }} convertido(s)</span>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary"></b-spinner>
        </div>

        <div v-else-if="loadError" class="text-center py-5 text-danger">
          <i class="mdi mdi-alert-circle-outline font-size-48 d-block mb-2"></i>
          Erro ao carregar leads. Tente recarregar a página.
        </div>

        <div v-else-if="leads.length === 0">
          <EmptyState
            icon="bx bx-check-circle"
            title="Nenhum lead convertido ainda"
            text="Quando um lead indicado virar contrato, ele aparece aqui com a comissão correspondente."
          />
        </div>

        <div v-else class="table-responsive">
          <b-table stacked="md" :items="leads" :fields="fields" hover responsive class="mb-0">

            <template #cell(afiliado_name)="{ item }">
              <span class="font-weight-medium">{{ item.afiliado_name }}</span>
            </template>

            <template #cell(name)="{ item }">
              <div class="font-weight-medium">{{ item.name }}</div>
              <small class="text-muted">{{ item.cpf }}</small>
            </template>

            <template #cell(contact)="{ item }">
              <div>{{ item.email || '—' }}</div>
              <small class="text-muted">{{ item.cellphone }}</small>
            </template>

            <template #cell(vehicle)="{ item }">
              <span class="text-capitalize">{{ vehicleLabel(item) }}</span>
              <div v-if="item.vehicle_plate" class="text-muted small">{{ item.vehicle_plate }}</div>
            </template>

            <template #cell(commission_value)="{ item }">
              <span class="font-weight-bold" style="color: var(--st-brand);">
                {{ formatMoney(item.commission_value) }}
              </span>
            </template>

            <template #cell(date_converted)="{ item }">
              <span class="text-success">{{ formatDate(item.date_converted) }}</span>
            </template>

            <template #cell(consultant_payment_date)="{ item }">
              <span v-if="item.payment_proof_url">
                <span class="text-success small d-block">
                  <i class="mdi mdi-cash-check mr-1"></i>{{ formatDate(item.consultant_payment_date) }}
                </span>
                <a href="javascript:void(0)" class="small" @click="openProofViewer(item)">
                  <i class="mdi mdi-file-document-outline mr-1"></i>Ver comprovante
                </a>
              </span>
              <span v-else class="text-muted small">Não pago</span>
            </template>

            <template #cell(actions)="{ item }">
              <b-button
                v-if="!item.payment_proof_url"
                size="sm"
                variant="success"
                @click="openPaymentModal(item)"
              >
                <i class="mdi mdi-cash-check mr-1"></i>
                Confirmar pagamento
              </b-button>
            </template>

          </b-table>
        </div>
      </div>
    </div>

    <FileViewerModal
      v-model="viewerVisible"
      :url="viewerLead && viewerLead.payment_proof_url"
      :name="viewerLead && viewerLead.payment_proof_name"
      title="Comprovante de Pagamento"
    />

    <!-- Payment confirmation modal -->
    <b-modal v-model="paymentModal" title="Confirmar Pagamento" hide-footer no-close-on-backdrop>
      <p class="mb-3" v-if="paymentLead">
        Confirme o pagamento da comissão de <strong>{{ paymentLead.name }}</strong>.
      </p>

      <div v-if="paymentError" class="alert alert-danger py-2 small">{{ paymentError }}</div>

      <b-form-group label="Valor da comissão *">
        <b-input-group prepend="R$">
          <b-form-input
            :value="paymentCommission"
            :state="commissionState"
            autocomplete="off"
            @input="paymentCommission = maskCurrency($event)"
          ></b-form-input>
        </b-input-group>
        <small class="text-muted d-block mt-1">
          Valor apurado pelo sistema: {{ formatMoney(systemCommission) }}. Você pode aumentar, mas nunca pagar menos do que esse valor.
        </small>
        <b-form-invalid-feedback :force-show="commissionState === false">
          A comissão não pode ser menor do que {{ formatMoney(systemCommission) }}.
        </b-form-invalid-feedback>
      </b-form-group>

      <p class="text-muted small mb-3">
        Após confirmado, o comprovante e o valor da comissão não poderão ser alterados.
      </p>

      <b-form-group label="Comprovante (PDF ou imagem) *">
        <b-form-file
          accept=".pdf,image/*"
          placeholder="Selecione um PDF ou imagem..."
          browse-text="Selecionar"
          @change="onPaymentFileChange"
        ></b-form-file>
      </b-form-group>

      <img v-if="paymentPreview && !paymentPreviewIsPdf" :src="paymentPreview" class="img-fluid rounded mb-2" style="max-height:220px;" />
      <iframe v-else-if="paymentPreview && paymentPreviewIsPdf" :src="paymentPreview" class="mb-2" style="width:100%;height:280px;border:1px solid #dee2e6;border-radius:4px;"></iframe>
      <small v-if="paymentFile" class="text-muted d-block mb-3">{{ paymentFile.name }}</small>

      <div class="text-right">
        <b-button variant="light" class="mr-2" @click="paymentModal = false">Cancelar</b-button>
        <b-button variant="success" :disabled="confirming || commissionState === false" @click="confirmPayment">
          <b-spinner v-if="confirming" small class="mr-1"></b-spinner>
          Confirmar Pagamento
        </b-button>
      </div>
    </b-modal>
  </Layout>
</template>
